import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Button,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Slide,
  TextField,
  Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '../../contexts/ThemeContext';
import { Todo } from '../../hooks/useTodoList';
import { useSafeArea } from '../../hooks/useSafeArea';

interface AddTodoDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (title: string, date: Date) => Promise<void>;
  editTodo?: Todo | null;
  initialDate?: Date;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const AddTodoDialog: React.FC<AddTodoDialogProps> = React.memo(({
  open,
  onClose,
  onSave,
  editTodo,
  initialDate = new Date(),
}) => {
  const { theme } = useTheme();
  // Safe Area para iOS (Notch/Dynamic Island)
  const safeArea = useSafeArea();
  const [title, setTitle] = useState('');
  // Use 'YYYY-MM-DD' format internally to match date input requirements if needed, 
  // but here we are using a custom date picker so we can stick to ISO string or Date object.
  // Sticking to YYYY-MM-DD string for consistency with previous logic references
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);

  // Custom Date Picker States
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Effect to initialize state when opening dialog or changing editTodo
  // DEPENDENCY FIX: Removed initialDate from dependencies to prevent reset on parent re-renders
  useEffect(() => {
    if (open) {
      if (editTodo) {
        setTitle(editTodo.title);
        setDate(editTodo.date.toISOString().split('T')[0]);
      } else {
        setTitle(prev => prev || '');
        setDate(prev => prev || initialDate.toISOString().split('T')[0]);
      }
    }
  }, [editTodo, open]); // Removed initialDate from dependencies!

  const handleSave = async () => {
    if (!title.trim()) return;

    // date is YYYY-MM-DD
    const [year, month, day] = date.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day);

    setSaving(true);
    try {
      await onSave(title, selectedDate);
      handleClose();
    } catch (error) {
      console.error('Erro ao salvar tarefa');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    // Reset date to initial only on close
    setDate(initialDate.toISOString().split('T')[0]);
    onClose();
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      slots={{
        transition: Transition
      }}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: theme.colors.background,
            display: 'flex',
            flexDirection: 'column',
          }
        }
      }}
    >
      {/* Header com AppBar fixo e Input de Título - IDÊNTICO ao EventsDialog */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: theme.colors.background,
          borderBottom: `1px solid ${theme.colors.border}`,
          paddingTop: `${safeArea.top}px`,
        }}
      >
        <Toolbar>
          {/* Botão Cancelar à esquerda */}
          <IconButton
            edge="start"
            onClick={handleClose}
            sx={{
              color: theme.colors.text.primary,
              mr: 2,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Título como Input Principal no Header */}
          <TextField
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={editTodo ? "Nome da tarefa" : "Nova tarefa..."}
            variant="standard"
            autoFocus // Opcional, mas comum em headers de criação
            sx={{
              flex: 1,
              '& .MuiInput-root': {
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: theme.colors.text.primary,
                '&:before': {
                  borderBottom: 'none',
                },
                '&:hover:not(.Mui-disabled):before': {
                  borderBottom: 'none',
                },
                '&.Mui-focused:after': {
                  borderBottom: `2px solid ${theme.colors.primary}`,
                },
              },
              '& .MuiInput-input::placeholder': {
                color: theme.colors.text.secondary,
                opacity: 0.7,
              },
            }}
          />

          <Button
            color="inherit"
            onClick={handleSave}
            disabled={!title.trim() || saving}
            sx={{
              color: theme.colors.primary,
              fontWeight: 'bold',
              opacity: (!title.trim() || saving) ? 0.5 : 1,
              ml: 2,
            }}
          >
            {saving ? '...' : 'Salvar'}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Conteúdo Body */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: 3,
          paddingLeft: `calc(24px + ${safeArea.left}px)`,
          paddingRight: `calc(24px + ${safeArea.right}px)`,
          paddingBottom: `calc(24px + ${safeArea.bottom}px)`,
        }}
      >
        <Box sx={{ maxWidth: '600px', margin: '0 auto' }}>
          <Typography variant="body1" fontWeight="bold" sx={{ color: theme.colors.text.primary, mb: 1 }}>
            Data:
          </Typography>

          {/* Botão para abrir o seletor de data customizado */}
          <Button
            onClick={() => setShowDatePicker(true)}
            variant="outlined"
            fullWidth
            sx={{
              justifyContent: 'flex-start',
              textAlign: 'left',
              padding: '16.5px 14px',
              borderColor: theme.colors.border,
              color: theme.colors.text.primary,
              textTransform: 'none',
              fontSize: '1rem',
              width: '100%',
              backgroundColor: theme.colors.card,
              '&:hover': {
                borderColor: theme.colors.primary,
                backgroundColor: theme.colors.card,
              }
            }}
          >
            {date ? new Date(date + 'T00:00:00').toLocaleDateString('pt-BR') : 'Selecionar data'}
          </Button>

        </Box>
      </Box>

      {/* Dialog de seleção de data customizado - CLONE do EventsDialog */}
      <Dialog
        open={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.radii.large,
            backgroundColor: theme.colors.background,
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, color: theme.colors.text.primary, textAlign: 'center' }}>
            Selecionar Data
          </Typography>

          {showDatePicker && (() => {
            // Internal date logic... (same as before)
            const currentDate = date ? new Date(date + 'T00:00:00') : new Date();

            return (
              <DatePickerContent
                initialDate={currentDate}
                onConfirm={(newDate) => {
                  setDate(newDate.toISOString().split('T')[0]);
                  setShowDatePicker(false);
                }}
                theme={theme}
              />
            );
          })()}
        </Box>
      </Dialog>
    </Dialog>
  );
});

// Helper component for the DatePicker content to standardise hooks usage
const DatePickerContent = ({ initialDate, onConfirm, theme }: { initialDate: Date, onConfirm: (date: Date) => void, theme: any }) => {
  const [selectedYear, setSelectedYear] = useState(initialDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(initialDate.getMonth());
  const [selectedDay, setSelectedDay] = useState(initialDate.getDate());

  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay(); // 0 = Domingo
  };

  const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
  const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDay }, (_, i) => i);

  // Generate year range (e.g., current year - 2 to + 10)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => currentYear - 2 + i);

  const handleDayClick = (day: number) => {
    setSelectedDay(day);
    // Auto confirm on day click? Or wait for button?
    // EventsDialog logic seemed to update state. Let's provide a confirm button or auto-close?
    // The previous code had a confirm. Let's replicate confirm action or direct selection.
    // EventsDialog snippet showed a "setDate" and "setShowDatePicker(false)" in "handleConfirm".
    // I will auto-confirm on day click for better UX or stick to manual?
    // User asked for "apenas DATA! DIA MES E ANO!".

    // Let's assume day click selects it, but maybe we need a confirm button like EventsDialog implies?
    // Wait, the previous snippet I read had "handleConfirm" but it wasn't called in the snippet I saw.
    // I'll add a Confirm button to be safe and explicit.
  };

  return (
    <Box>
      {/* Seletor de Mês e Ano */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <TextField
          select
          fullWidth
          label="Mês"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
          SelectProps={{
            native: true,
          }}
          sx={{
            '& .MuiInputLabel-root': {
              backgroundColor: theme.colors.background,
              px: 0.5,
            },
          }}
        >
          {months.map((month, index) => (
            <option key={index} value={index}>
              {month}
            </option>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          label="Ano"
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          SelectProps={{
            native: true,
          }}
          sx={{
            '& .MuiInputLabel-root': {
              backgroundColor: theme.colors.background,
              px: 0.5,
            },
          }}
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </TextField>
      </Box>

      {/* Grade de dias */}
      <Box sx={{ mb: 3 }}>
        {/* Cabeçalho dos dias da semana */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
          {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((day, i) => (
            <Box
              key={i}
              sx={{
                textAlign: 'center',
                fontWeight: 'bold',
                color: theme.colors.text.secondary,
                fontSize: '0.875rem',
              }}
            >
              {day}
            </Box>
          ))}
        </Box>

        {/* Grade de dias */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {emptyDays.map((_, i) => (
            <Box key={`empty-${i}`} />
          ))}
          {days.map((day) => (
            <Button
              key={day}
              onClick={() => handleDayClick(day)}
              sx={{
                minWidth: 40,
                height: 40,
                p: 0,
                backgroundColor: selectedDay === day ? theme.colors.primary : 'transparent',
                color: selectedDay === day ? theme.colors.text.contrast : theme.colors.text.primary,
                borderRadius: theme.radii.small,
                '&:hover': {
                  backgroundColor: selectedDay === day ? theme.colors.primary : theme.colors.card,
                  opacity: 0.9,
                },
              }}
            >
              {day}
            </Button>
          ))}
        </Box>
      </Box>

      <Button
        onClick={() => onConfirm(new Date(selectedYear, selectedMonth, selectedDay))}
        fullWidth
        variant="contained"
        sx={{
          backgroundColor: theme.colors.primary,
          color: theme.colors.text.contrast,
          fontWeight: 'bold',
          '&:hover': {
            backgroundColor: theme.colors.primary,
            opacity: 0.8,
          }
        }}
      >
        Confirmar
      </Button>
    </Box>
  );
};
